import styles from "./CartProductCard.module.css"
import React, { useRef } from 'react';
import { X, PencilLine } from 'lucide-react';
import { getPrimaryProductImage } from "../../../services/productImageService";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { useTranslation } from "react-i18next";
import { normalizeDate } from "../../../utils/calendar";
import { useMemo, useCallback } from "react";
import { useState, useEffect } from 'react';
import { cartChange } from "../../../utils/cart";
import { CartItem } from "../../../types/cart.types";
import { defaultMinTime, defaultMaxTime } from "../../../constants/times";
import { useOrderContext } from "../../../context/OrderContext";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { CartActivityProps } from "../../../types/activity.types";


/**
* Cart card for activities
*
* @param props CartActivityProps
* @param {Product} props.product - Product
* @param {string} props.time - Delivery time
* @param {string} props.date - Delivery date
* @param {boolean} props.popup - Optional flag whether its open on popup
* @returns TSX.Element
*/
const CartActivityCard = ({ product, time, date, popup = true } : CartActivityProps) => {    
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { i18n } = useTranslation(); // Translation
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal toggle 
    // Find the correct items
    const serverItem: CartItem | undefined = useMemo(() => {
        const itemsForDate: CartItem[] = cartData?.items?.[date] ?? [];
        return itemsForDate.find(
        (cartProduct) => cartProduct.id === product.id && cartProduct.time === time && cartProduct.category === "activities"
        );
    }, [cartData?.items, date, product.id, time, "activities"]);

    // Cart amount
    const [cartAmount, setCartAmount] = useState<number>(() => serverItem?.amount ?? 0);
    // Track if the cart data amount is changing
    const isAmountChangingRef = useRef(false);

    // Update cartAmount on CartData change and not changing - get rid of number flash
    useEffect(() => {
        if (!cartLoaded) return;
        const next = serverItem?.amount ?? 0;
        if (!isAmountChangingRef.current && next !== cartAmount) {
            setCartAmount(next);
        }
    }, [cartLoaded, serverItem?.amount]); 

    // Runs every time the users adds or deletes - reruns the timeout till the clicks dont stop - updates afterwards
    const debouncedReload = useMemo(() => {
        let refreshTimeout: number | null = null;
        return () => {
            if (refreshTimeout) window.clearTimeout(refreshTimeout);
            refreshTimeout = window.setTimeout(() => {
                reloadCart();
                refreshTimeout = null;
            }, 250);
        };
    }, [reloadCart]);

    // Change cart amount - using callback and Promise.resolve to unset changing only after the request is complete
    const handleCart = useCallback(
        (amount: number) => {
            setCartAmount(amount);           
            isAmountChangingRef.current = true; // Set changing ref
            Promise.resolve(
                cartChange(
                    cartData,
                    product.id,
                    normalizeDate(date),
                    time,
                    amount,
                    "activities",
                    normalizeDate(date),
                    time,
                    debouncedReload
                )
            )
            .finally(() => {
            isAmountChangingRef.current = false; // Unset changing ref
            });
        },
        [cartData, product.id, normalizeDate(date), time, "activities", debouncedReload]
    );
    
    const buttonRef = useRef<HTMLDivElement>(null)
    const actionButtonsRef = useRef<HTMLDivElement>(null)
    // Handle opening detail
    const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!buttonRef.current?.contains(e.target as Node) && !actionButtonsRef.current?.contains(e.target as Node)) {
            openDetail && openDetail({id: product.id, type: "activities", time: time, date: date }, false, popup); 
        }
    }

    return(
        <>
        <div className={styles.CartProduct} key={product.id} onClick={handleOpen}>
            <div className={styles.CartProductImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
            <div className={styles.CartProductContent}>
                <div className={styles.CartProductInfo}>
                    <p className={styles.CartProductTitle}>{i18n.language === "en" ? product.title_en || "N/A" : product.title}</p>
                    <div className={styles.CartProductButtons} ref={actionButtonsRef}>
                        <button
                            className={styles.CartProductButton}
                            onClick={() =>
                                openTimeModal(
                                    {
                                        type: "single",
                                        date: normalizeDate(date),
                                        time: time,
                                        productId: product.id,
                                        prodType: "activities",
                                        restrictions: { min: defaultMinTime, max: defaultMaxTime },
                                    },
                                    { openOnTop: popup }
                                )
                            }
                        >
                            <PencilLine/>
                        </button>
                        <button className={styles.CartProductButton} onClick={()=>cartChange(cartData, product.id, normalizeDate(date), time, 0, "activities", undefined, undefined, reloadCart)} >
                            <X/>
                        </button>
                    </div>
                </div>
                <div className={styles.CartProductOrderInfo}>
                </div>
            </div>
        </div>
        </>
    )
}   

export default CartActivityCard
